import { Injectable } from '@angular/core';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { environment } from '@environments/environment';
import { MonitoringService } from '@app/shared/common/telemetry-implementation/MonitoringService ';
import { initAppLog } from './app-log.util';

@Injectable({
    providedIn: 'root',
})
export class AppLoggerService {
    constructor(private readonly monitoring: MonitoringService) {
        initAppLog(this);
    }

    log(message?: unknown, ...args: unknown[]): void {
        this.write('log', message, args);
    }

    warn(message?: unknown, ...args: unknown[]): void {
        this.write('warn', message, args);
    }

    error(message?: unknown, ...args: unknown[]): void {
        this.write('error', message, args);
    }

    debug(message?: unknown, ...args: unknown[]): void {
        this.write('debug', message, args);
    }

    info(message?: unknown, ...args: unknown[]): void {
        this.write('info', message, args);
    }

    private write(level: 'log' | 'warn' | 'error' | 'debug' | 'info', message?: unknown, args: unknown[] = []): void {
        const formatted = this.formatMessage(message, args);

        switch (level) {
            case 'error':
                this.logToAbp('error', formatted);
                this.logToAppInsights(formatted, true, message, args);
                if (!environment.production) {
                    console.error(message, ...args);
                }
                break;
            case 'warn':
                this.logToAbp('warn', formatted);
                if (!environment.production) {
                    console.warn(message, ...args);
                }
                break;
            case 'debug':
                this.logToAbp('debug', formatted);
                if (!environment.production) {
                    console.debug(message, ...args);
                }
                break;
            case 'info':
                this.logToAppInsights(formatted, false);
                if (!environment.production) {
                    console.info(message, ...args);
                }
                break;
            default:
                this.logToAppInsights(formatted, false);
                if (!environment.production) {
                    console.log(message, ...args);
                }
                break;
        }
    }

    private formatMessage(message?: unknown, args: unknown[] = []): string {
        return [message, ...args]
            .map((part) => {
                if (part instanceof Error) {
                    return part.stack || part.message;
                }
                if (typeof part === 'string') {
                    return part;
                }
                try {
                    return JSON.stringify(part);
                } catch {
                    return String(part);
                }
            })
            .join(' ');
    }

    private logToAbp(level: 'error' | 'warn' | 'debug', message: string): void {
        if (typeof abp === 'undefined' || !abp.log?.[level]) {
            return;
        }
        abp.log[level](message);
    }

    private logToAppInsights(
        message: string,
        isError: boolean,
        originalMessage?: unknown,
        args: unknown[] = []
    ): void {
        if (!this.monitoring?.isenable) {
            return;
        }

        if (isError) {
            const error =
                originalMessage instanceof Error
                    ? originalMessage
                    : args.find((arg): arg is Error => arg instanceof Error) ||
                      new Error(message);
            this.monitoring.logException(error, SeverityLevel.Error);
            return;
        }

        this.monitoring.logTrace(message);
    }
}
